import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import Button from "components/ui/button/Button";
import { SocialProviderType } from "lib/auth/authClient";
import { sendGa4Event } from "lib/ga4Tracking";

const PROVIDERS: { provider: SocialProviderType; label: string }[] = [
  { provider: "google", label: "Google" },
  { provider: "microsoft", label: "Microsoft" },
];

// GA4/GTM: continue_with_google / continue_with_microsoft.
// Exported so the login page can report a provider it starts on the user's behalf the same way.
export function trackSocialSignIn(mode: "login" | "register", provider: SocialProviderType) {
  const form =
    mode === "login" ? { id: "login-form", name: "Login" } : { id: "signup-form", name: "Signup" };
  sendGa4Event({ event: `continue_with_${provider}`, form });
}

type Props = {
  mode: "login" | "register";
  onSelect: (provider: SocialProviderType) => void;
  /**
   * Which social providers to offer. Defaults to all of them; the login page narrows it once it
   * knows the user's company. Renders nothing (divider included) when empty and SSO is absent.
   */
  providers?: SocialProviderType[];
  /**
   * Starts enterprise SSO. Only passed when the company has a verified IdP *and* another way in —
   * when it's the only one, the login page starts it rather than rendering a button.
   */
  onEnterpriseSso?: () => void;
  /** Set while a hand-off is already in flight, so a slow one can't be started a second time. */
  disabled?: boolean;
};

/** The sign-in methods that hand the browser to an identity provider rather than taking input here. */
export function ExternalSignInButtons({
  mode,
  onSelect,
  providers,
  onEnterpriseSso,
  disabled,
}: Props) {
  const shown = providers ? PROVIDERS.filter((p) => providers.includes(p.provider)) : PROVIDERS;

  const handleSelect = (provider: SocialProviderType) => {
    trackSocialSignIn(mode, provider);
    onSelect(provider);
  };

  if (shown.length === 0 && !onEnterpriseSso) return null;

  return (
    <Flex flexDirection="column" gap={8}>
      {onEnterpriseSso && (
        <Button
          icon="building07"
          buttonType="secondary"
          justifyContent="center"
          width="100%"
          disabled={disabled}
          onClick={onEnterpriseSso}
        >
          Single sign-on
        </Button>
      )}
      {shown.length > 0 && (
        <Flex gap={8}>
          {shown.map(({ provider, label }) => (
            <Button
              key={provider}
              icon={provider}
              buttonType="secondary"
              justifyContent="center"
              width={shown.length === 1 ? "100%" : "50%"}
              disabled={disabled}
              onClick={() => handleSelect(provider)}
            >
              {label}
            </Button>
          ))}
        </Flex>
      )}
      <Divider>Or</Divider>
    </Flex>
  );
}

// "or" divider with a rule either side, sitting between the email form and the provider buttons.
const Divider = styled(Flex)(({ theme }) => ({
  alignItems: "center",
  gap: 12,
  color: theme.colors.sysTextSecondary,
  marginTop: 12,
  fontSize: 12,
  "&::before, &::after": {
    content: '""',
    flex: 1,
    height: 1,
    backgroundColor: theme.colors.sysBorderDecorative,
  },
}));
